/*
 * Haye Nii: makes the big name in cart lines fit its box (task 26).
 * Used by snippets/cart-line-details.liquid (cart page, "added to cart" popup, cart drawer).
 *
 * Why: many name fonts draw marks far above and below the normal letter box (Gurmukhi/Hindi matras, Urdu
 * nastaliq, Punjabi fonts 8 and 9 swirls) or start left of it. A fixed line height can't fit all 64 fonts.
 * So, like the product page preview, we measure the real ink of each name in its font and:
 *  1. give it exactly enough space above, below, left and right (nothing overlaps or gets cut),
 *  2. make every name the same height (the ink, not the font size: fonts draw letters at very different sizes),
 *  3. make it smaller only when it is wider than its column (it always stays on one line).
 * Without this script the name still shows (normal CSS, a bit of extra space), just not measured.
 */
(function () {
  if (window.hayeniiCartLines) return;
  window.hayeniiCartLines = true;

  var MIN_PX = 8; // only very long names on the narrowest phones get this small; it must always fit its column
  var ctx = document.createElement('canvas').getContext('2d');
  if (!ctx || !('measureText' in ctx)) return;
  var lastWidth = new WeakMap();

  function fit(name) {
    var text = name.getAttribute('data-hn-text');
    var box = name.parentElement;
    if (!text || !box || !name.isConnected) return;
    var room = box.clientWidth;
    if (!room) return; // hidden (e.g. popup not open yet): the size watcher calls again when it shows

    // Start from the CSS size every time (screen size may have changed)
    name.classList.remove('is-fit');
    name.style.fontSize = '';
    name.style.lineHeight = '';
    name.style.padding = '';
    var cs = getComputedStyle(name);
    var size = parseFloat(cs.fontSize);
    ctx.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + size + 'px ' + cs.fontFamily;
    ctx.direction = cs.direction === 'rtl' ? 'rtl' : 'ltr';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    var m = ctx.measureText(text);
    if (!m.width) return;

    // Same height for every language and font (owner 2026-10-18: English script looked tiny, Punjabi huge): scale the
    // font so the name's real ink (letters + marks + swirls) is the same height, --hn-name-ink px (set in the CSS)
    var target = parseFloat(cs.getPropertyValue('--hn-name-ink'));
    var inkH = (m.actualBoundingBoxAscent || 0) + (m.actualBoundingBoxDescent || 0);
    if (target > 0 && inkH > 0) {
      var even = Math.min(2.5, Math.max(0.4, target / inkH));
      size = Math.round(size * even * 10) / 10;
      ctx.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + size + 'px ' + cs.fontFamily;
      m = ctx.measureText(text);
    }

    var inkLeft = Math.max(0, m.actualBoundingBoxLeft || 0); // ink that starts left of the letters' box
    var inkRight = Math.max(0, (m.actualBoundingBoxRight || m.width) - m.width); // ink past the right end
    var needW = inkLeft + m.width + inkRight;

    // Too wide for the column: make it smaller (sizes scale evenly with the font size)
    var scale = 1;
    var maxW = room - 2;
    if (needW > maxW) {
      scale = Math.max(MIN_PX / size, maxW / needW);
    }
    var px = Math.floor(size * scale * 10) / 10; // round down, so it never ends up a hair too wide
    var k = px / size;

    // Room above and below: where the baseline sits in a line of this height, then how far the ink goes
    var lineH = Math.round(px * 1.25);
    var fontA = (m.fontBoundingBoxAscent != null ? m.fontBoundingBoxAscent : size * 0.8) * k;
    var fontD = (m.fontBoundingBoxDescent != null ? m.fontBoundingBoxDescent : size * 0.2) * k;
    var baseline = (lineH - (fontA + fontD)) / 2 + fontA;
    var inkTop = baseline - (m.actualBoundingBoxAscent || size * 0.8) * k;
    var inkBottom = baseline + (m.actualBoundingBoxDescent || size * 0.2) * k;
    var padTop = Math.ceil(Math.max(0, -inkTop)) + 2;
    var padBottom = Math.ceil(Math.max(0, inkBottom - lineH)) + 2;

    name.style.fontSize = px + 'px';
    name.style.lineHeight = lineH + 'px';
    name.style.padding = padTop + 'px ' + Math.ceil(inkRight * k) + 'px ' + padBottom + 'px ' + Math.ceil(inkLeft * k) + 'px';
    name.classList.add('is-fit');
  }

  function fitWhenFontReady(name) {
    var cs = getComputedStyle(name);
    var spec = cs.fontStyle + ' ' + cs.fontWeight + ' ' + parseFloat(cs.fontSize) + 'px ' + cs.fontFamily;
    fit(name);
    if (document.fonts && document.fonts.load) {
      document.fonts.load(spec, name.getAttribute('data-hn-text') || '').then(function () { fit(name); }, function () {});
    }
  }

  // Fit again whenever a cart line's width changes: page load, screen turned, popup opened, cart redrawn
  var watcher = 'ResizeObserver' in window
    ? new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          var w = Math.round(entry.contentRect.width);
          if (lastWidth.get(entry.target) === w) return;
          lastWidth.set(entry.target, w);
          var name = entry.target.querySelector('[data-hn-text]');
          if (name) fitWhenFontReady(name);
        });
      })
    : null;

  function scan(root) {
    (root || document).querySelectorAll('.hn-line').forEach(function (line) {
      if (line.hnWatched) return;
      line.hnWatched = true;
      var name = line.querySelector('[data-hn-text]');
      if (!name) return;
      fitWhenFontReady(name);
      if (watcher) watcher.observe(line);
    });
  }

  // Kite free gift box: "Free - Mystery Gift (Worth $30)" -> title "Free Mystery Gift", small "Worth $30" under it (CAD only)
  // (owner 2026-10-18: the bold title wrapped onto 2 lines). Kite's own words, only split; the Claim button is untouched.
  function tidyGift(root) {
    (root || document).querySelectorAll('.gfgFreeGiftMsgTitle').forEach(function (title) {
      var m = (title.textContent || '').match(/^\s*(.*?)\s*\(\s*(worth[^)]*)\)\s*$/i);
      if (!m) return;
      var sub = title.parentElement && title.parentElement.querySelector('.gfgFreeGiftMsgSubTitle');
      if (!sub || sub.textContent.trim()) return; // Kite shows its own subtitle: leave the box as it is
      title.textContent = m[1].replace(/\s+-\s+/, ' ');
      // "Worth $30" is the gift's CAD price: in USD it is $26, in INR Rs. 2,500, so it shows only in CAD (break-it audit S1)
      var cur = window.Shopify && Shopify.currency && Shopify.currency.active;
      if (cur === 'CAD') sub.textContent = m[2];
    });
  }

  function fitAll() {
    document.querySelectorAll('.hn-line [data-hn-text]').forEach(fit);
  }

  // The cart page, popup and drawer are redrawn by the theme after each change: watch for new lines
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].addedNodes.length) { scan(document); tidyGift(document); return; }
    }
  }).observe(document.body, { childList: true, subtree: true });
  // Also without the size watcher: screen size changes, late fonts, and the popup opening (it is hidden until then)
  var timer;
  window.addEventListener('resize', function () {
    clearTimeout(timer);
    timer = setTimeout(fitAll, 120);
  });
  if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', fitAll);
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[name="add"], .product-form__submit, cart-notification, #cart-icon-bubble')) {
      setTimeout(fitAll, 400);
      setTimeout(fitAll, 1200);
    }
  });
  scan(document);
  tidyGift(document);
})();
